<template>
  <div class="viewer-container">
    <!-- Left Column: Thumbnails -->
    <div class="thumbnails-column elevation-5">
      <div class="thumbnails-scroll">
        <div
          v-for="(file, index) in Files"
          :key="index"
          class="thumbnail-item"
          :class="{ active: selectedFileIndex === index }"
					@click="selectAndScroll(index)"
					:style="{ '--aspect-ratio': file.Metadata?.AspectRatio || '16/9' }"
        >
          <v-img
            v-if="file.Metadata && file.Metadata.ThumbnailUrl"
            :src="file.Metadata.ThumbnailUrl"
            :alt="file.FileName"
						contain
            class="rounded"
          />

        </div>
      </div>
    </div>

    <!-- Right Column: Main Image Display -->
    <div class="main-image-column elevation-5" >
      <div class="main-image-scroll" ref="mainImageScroll">
        <div v-for="(file, index) in Files" :key="index"
					class="main-image-container"
					:ref="`image-${index}`"
					:style="{ '--aspect-ratio': file.Metadata?.AspectRatio || '16/9' }"
					>
          <LazyVImg v-if="0"
            :src="file.Metadata.PreviewFile"
						:high-res-src="file.Uri"
            :alt="file.DisplayName"
						:eager="index < 5"
          />
					<v-img
						:lazy-src="file.Metadata.ThumbnailUrl"
						:src="file.Uri"
						:alt="file.DisplayName"
						:eager="index < 5"
					/>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CrudClient from "@/services/CrudClient";
import { mapActions } from "vuex";
import LazyVImg from "@/views/Portfolio/LazyVImg.vue";

export default {
	name: "Viewer",
	components: { LazyVImg },
	data() {
		return {
			LoadingImages: false,
			Files: [],
			flatFilesView: false,
			selectedFileIndex: 0
		}
	},
	computed: {
		EventId() {
			return parseInt(this.$route.params.eventId);
		},

	},
	created() {
		this.SgpEventsService = new CrudClient("SGP/PortfolioEvents");
	},
	async mounted() {
		this.eventDetails = await this.SgpEventsService.Get(this.EventId);

		await this.LoadStorageFolderFiles();

		// Auto-select first file if available
		if (this.Files.length > 0) {
			this.selectedFileIndex = 0;
		}
	},

	methods: {
		...mapActions(["snackSuccess", "snackError"]),

		async LoadStorageFolderFiles() {
			this.LoadingImages = true;
			try {
				const parameters = "orderBy=CustomSortOrder";

				this.Files = await this.SgpEventsService.Get(
					this.EventId,
					"Files?" + parameters
				);
			} catch (error) {
				this.snackError({ Text: this.$t("snackError_CannotLoadData") });
				this.$captureError(error);
			} finally {
				this.LoadingImages = false;
			}
		},

		selectAndScroll(index) {
			this.selectedFileIndex = index;
			this.$nextTick(() => {
				const element = this.$refs[`image-${index}`];
				if (element && element[0]) {
					element[0].scrollIntoView({ behavior: "smooth", block: "center" });
				}
			});
		},

	}
}
</script>

<style scoped lang="scss">
.viewer-container {
	display: flex;
	width: 100%;
	gap: 16px;
	padding: 16px;

}

.thumbnails-column {
	flex: 0 0 15%;
	min-width: 100px;
	background: white;
	border-radius: 8px;
	overflow: hidden;
	height: 80vh;
	min-height: 600px;
}

.thumbnails-scroll {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 12px;
	height: 100%;
	overflow-y: auto;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}

	&::-webkit-scrollbar-thumb {
		background: #888;
		border-radius: 4px;

		&:hover {
			background: #555;
		}
	}
}

.thumbnail-item {
	display: flex;
	flex-direction: column;
	gap: 8px;
	cursor: pointer;
	border: 2px solid transparent;
	border-radius: 6px;
	padding: 2px;
	transition: all 0.3s ease;
	aspect-ratio: var(--aspect-ratio, 16/9);

	&:hover {
		background-color: #f0f0f0;
		border-color: #ddd;
	}

	&.active {
		background-color: #e3f2fd;
		border-color: #1976d2;
	}
}

.main-image-column {
	flex: 1;
	background: white;
	border-radius: 8px;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	height: 80vh;
	min-height: 600px;
}

.main-image-scroll {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow-y: auto;
	padding: 16px;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: #f1f1f1;
	}

	&::-webkit-scrollbar-thumb {
		background: #888;
		border-radius: 4px;

		&:hover {
			background: #555;
		}
	}
}

.main-image-container {
	display: flex;
	flex-direction: column;
	aspect-ratio: var(--aspect-ratio, 16/9);
	width: 100%;
	padding-bottom: 5px;
}

.main-image {
	width: 100%;
	max-height: 600px;
	object-fit: contain;
	border-radius: 8px;
	background-color: #f5f5f5;
	cursor: pointer;
	transition: all 0.3s ease;
	border: 3px solid transparent;

	&:hover {
		opacity: 0.8;
	}

	&.highlighted {
		border-color: #1976d2;
		box-shadow: 0 0 12px rgba(25, 118, 210, 0.3);
	}
}

.image-placeholder {
	width: 100%;
	height: 400px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border-radius: 8px;
	color: #999;
	font-size: 16px;
}

.no-selection {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	color: #999;
	font-size: 16px;
}

// Mobile responsive styles
@media (max-width: 960px) and (orientation: portrait)  {
	.viewer-container {
		flex-direction: column;
		padding: 12px;
		gap: 12px;
		height: calc(100vh - 65px);
	}

	.main-image-column {
		flex: 0 0 80%;
		height: auto;
		min-height: unset;
		order: 1;
	}

	.thumbnails-column {
		flex: 0 0 20%;
		min-width: unset;
		height: auto;
		min-height: unset;
		order: 2;
	}

	.thumbnails-scroll {
		flex-direction: row;
		overflow-x: auto;
		overflow-y: hidden;
		height: auto;
		min-height: 100px;
	}

	.thumbnail-item {
		flex: 0 0 auto;
		width: 100px;
		aspect-ratio: var(--aspect-ratio, 16/9);
	}

	.main-image-scroll {
		padding: 12px;
		overflow-x: hidden;
	}
}
</style>
